import AddIcon from "@mui/icons-material/Add";
import { Box, Typography } from "@mui/material";
import { ValidatedTextField } from "components/common/inputs/validatedTextField";
import { useTenantTranslation } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { Serialiser } from "hooks/serialisers/types";
import { useState } from "react";
import {
  MaterialLimit,
  MaterialLimitBounds,
  ParameterConflict,
} from "src/store/api/generatedApi";
import { MarkerDot } from "./MarkerDot";
import { ConflictMarkerDot } from "./ConflictMarkerDot";
import {
  HARD_INEQUALITY,
  InequalityLayout,
  slotInputSx,
  SOFT_INEQUALITY,
} from "./MaterialLimitInequality";
import {
  boundForScope,
  differsFromScrapchefDefault,
  hasBoundsOutsideScope,
  MATERIAL_LIMIT_BOUND_FIELDS,
  MaterialLimitBoundField,
  MaterialLimitScope,
} from "./materialLimitMatrix";
import { ConflictDescription } from "./ConflictDescription";
import { conflictKey } from "./parameterConflicts";

const MATERIAL_SYMBOL = "x";

interface Props {
  limit: MaterialLimit | undefined;
  scrapchefLimit: MaterialLimit | undefined;
  conflicts: ParameterConflict[];
  limitLabel: string;
  scope: MaterialLimitScope;
  hardnessView: boolean;
  activeFields: MaterialLimitBoundField[];
  basketNumberById: Record<number, number>;
  boundSerialiser: Serialiser<number | null, never>;
  onChangeField: (
    field: MaterialLimitBoundField | "hardness",
    value: number | null
  ) => void;
  onAdd: () => void;
}

/** The reduced inequality the cells show — only the bounds that are set, in the display unit. */
const boundInequality = (
  bound: MaterialLimitBounds,
  serialiser: Serialiser<number | null, never>
): string => {
  const left = [
    bound.min_mass != null
      ? `${serialiser.format(bound.min_mass)} ${HARD_INEQUALITY}`
      : null,
    bound.soft_min_mass != null
      ? `${serialiser.format(bound.soft_min_mass)} ${SOFT_INEQUALITY}`
      : null,
  ].filter((part) => part !== null);
  const right = [
    bound.soft_max_mass != null
      ? `${SOFT_INEQUALITY} ${serialiser.format(bound.soft_max_mass)}`
      : null,
    bound.max_mass != null
      ? `${HARD_INEQUALITY} ${serialiser.format(bound.max_mass)}`
      : null,
  ].filter((part) => part !== null);
  return [...left, MATERIAL_SYMBOL, ...right].join(" ");
};

export const MaterialLimitCell = ({
  limit,
  scrapchefLimit,
  conflicts,
  limitLabel,
  scope,
  hardnessView,
  activeFields,
  basketNumberById,
  boundSerialiser,
  onChangeField,
  onAdd,
}: Props) => {
  const { t } = useTenantTranslation();
  const [hovered, setHovered] = useState(false);
  // Focus keeps the input mounted after the pointer leaves the cell.
  const [focused, setFocused] = useState(false);
  const expanded = hovered || focused;
  const serialiser = useNumberSerialiser({
    min: 0,
    default: { value: null, text: "" },
    decimalPlaces: 3,
  });

  if (limit === undefined) {
    return (
      <Box
        onClick={onAdd}
        title={t("createMaterialLimit")}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.disabled",
          cursor: "pointer",
          "&:hover": { color: "primary.main" },
        }}
      >
        <AddIcon fontSize="small" />
      </Box>
    );
  }

  const limitSummary = (summarised: MaterialLimit) => (
    <Box>
      {[...summarised.bounds]
        .sort((a, b) => (a.basket_id ?? -1) - (b.basket_id ?? -1))
        .map((scopeBound) => (
          <Typography
            key={scopeBound.basket_id ?? "mix"}
            variant="caption"
            display="block"
          >
            {scopeBound.basket_id == null
              ? t("mix")
              : `${t("basket")} ${basketNumberById[scopeBound.basket_id] ?? "?"}`}
            : {boundInequality(scopeBound, boundSerialiser)}
          </Typography>
        ))}
      <Typography variant="caption" display="block">
        {t("hardness")}: {summarised.hardness}
      </Typography>
    </Box>
  );

  const scrapchefMarker = differsFromScrapchefDefault(limit, scrapchefLimit) ? (
    <MarkerDot
      corner="top-left"
      color="secondary.main"
      tooltip={
        <Box>
          <Typography variant="subtitle2">{t("current")}</Typography>
          {limitSummary(limit)}
          <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
            {t("scrapchefDefault")}
          </Typography>
          {scrapchefLimit === undefined ? (
            <Typography variant="caption">—</Typography>
          ) : (
            limitSummary(scrapchefLimit)
          )}
        </Box>
      }
    />
  ) : null;

  const conflictMarker =
    conflicts.length > 0 ? (
      <ConflictMarkerDot
        conflicts={conflicts}
        corner="bottom-left"
        tooltip={
          <Box>
            {conflicts.map((conflict) => (
              <Box key={conflictKey(conflict)} sx={{ mb: 0.5 }}>
                <ConflictDescription
                  conflict={conflict}
                  limitLabel={limitLabel}
                />
              </Box>
            ))}
          </Box>
        }
      />
    ) : null;

  if (hardnessView) {
    return (
      <Box
        onClick={(event) => {
          event.stopPropagation();
        }}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {scrapchefMarker}
        {conflictMarker}
        <Box
          sx={{
            border: 1,
            borderColor: "primary.main",
            borderRadius: 1,
            px: 0.5,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ValidatedTextField
            value={limit.hardness}
            setValue={(next) => {
              if (next !== null) {
                onChangeField("hardness", next);
              }
            }}
            serialiser={serialiser}
            textFieldProps={{ variant: "standard", sx: slotInputSx }}
          />
        </Box>
      </Box>
    );
  }

  const bound = boundForScope(limit, scope);
  const boundEmpty =
    bound === undefined ||
    MATERIAL_LIMIT_BOUND_FIELDS.every((field) => bound[field] == null);
  // Interaction exposes every bound field, including fields that are empty in all rows.
  const displayFields = expanded ? MATERIAL_LIMIT_BOUND_FIELDS : activeFields;

  return (
    <Box
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false);
        }
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: expanded ? "flex-start" : "center",
      }}
    >
      {scrapchefMarker}
      {conflictMarker}
      {hasBoundsOutsideScope(limit, scope) ? (
        <MarkerDot
          corner="top-right"
          color="info.main"
          tooltip={limitSummary(limit)}
        />
      ) : null}
      {boundEmpty && !expanded ? (
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      ) : (
        <Box
          sx={
            expanded
              ? {
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                }
              : undefined
          }
        >
          <InequalityLayout
            fields={displayFields}
            renderSlot={(field) => (
              <ValidatedTextField
                value={bound?.[field] ?? null}
                setValue={(next) => {
                  onChangeField(field, next);
                }}
                serialiser={boundSerialiser}
                textFieldProps={{ variant: "standard", sx: slotInputSx }}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};
